import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileUp, Loader2, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invId } from "@/lib/invoice";

export default function ImportPage() {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("PDF trop volumineux (20 Mo maximum)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/import", fd, { timeout: 180000 });
      toast.success("Facture importée — extraction IA terminée");
      const doc = data?.invoice || data;
      const id = invId(doc);
      navigate(id ? `/app/factures/${id}` : "/app/factures");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || d?.message).filter(Boolean).join(", ");
      else if (!e?.response) msg = "Serveur injoignable ou délai dépassé — réessayez avec un PDF plus léger";
      msg = msg || "Import impossible";
      toast.error(msg, { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="import-page" className="max-w-3xl">
      <p className="overline-label mb-2">Import</p>
      <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl mb-3">Importer une facture PDF</h1>
      <p className="text-zinc-400 text-sm md:text-base max-w-xl mb-10">
        Déposez votre PDF : l'IA extrait le vendeur, l'acheteur, chaque ligne, la TVA et l'octroi de mer.
        Vous pourrez ensuite convertir en Factur-X et envoyer via votre PDP.
      </p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="dropzone"
        className={`cursor-pointer rounded-3xl border-2 border-dashed p-14 md:p-20 text-center transition-colors duration-300 ${
          dragging ? "border-[#00D4FF] bg-[#00D4FF]/5" : "border-white/15 bg-[#0A0E17] hover:border-[#2563EB]/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 text-[#00D4FF] animate-spin" />
            <p className="font-display text-lg">Extraction IA en cours…</p>
            <p className="text-sm text-zinc-500">Analyse des lignes, taux de TVA et octroi de mer.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#2563EB]/15 border border-[#2563EB]/30 text-[#00D4FF]">
              <FileUp className="h-7 w-7" />
            </span>
            <p className="font-display text-lg">Glissez votre PDF ici</p>
            <p className="text-sm text-zinc-500">ou cliquez pour parcourir vos fichiers</p>
          </div>
        )}
      </motion.div>

      <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-[#0A0E17] p-5" data-testid="import-note">
        <ScanSearch className="h-5 w-5 text-[#00D4FF] shrink-0 mt-0.5" />
        <p className="text-sm text-zinc-400 leading-relaxed">
          Après extraction, vérifiez les données détectées, appliquez l'autocorrection IA si besoin,
          puis convertissez au format Factur-X (EN 16931) avant envoi à votre Plateforme de
          Dématérialisation Partenaire.
        </p>
      </div>
    </div>
  );
}
